import { ICommonSettings, IDocumentManagementSettings } from "@kuika/kuika-cl-model/src/models/common-config";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter, ConfigModalGroupHeading } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigDocumentManagementSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingSetting?: IDocumentManagementSettings;
}

export interface IConfigDocumentManagementSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingDocumentManagementId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigDocumentManagementSettings extends React.PureComponent<
  IConfigDocumentManagementSettingsProps,
  IConfigDocumentManagementSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigDocumentManagementSettingsProps) {
    super(props);
    this.state = { saveStatus: "idle", deleteStatus: "idle", editingSetting: undefined };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigDocumentManagementSettingsProps) {
    if (prevProps.editingDocumentManagementId && !this.props.editingDocumentManagementId) {
      this.formRef.current?.resetFields();
    }

    if (prevProps.editingDocumentManagementId !== this.props.editingDocumentManagementId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
      return;
    }

    if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingDocumentManagementId) {
      this.setState({ editingSetting: undefined }, () => {
        this.formRef.current?.resetFields();
        this.formRef.current?.setFieldsValue({
          requireAuditKey: false,
          maxFileSizeBytes: 524288000,
          auditLogRetentionDays: 90,
          onlyOffice: { enabled: false },
          collabora: { enabled: false },
          drawio: { serverUrl: "" }
        });
      });
      return;
    }

    const list = _.cloneDeep(this.props.commonSettings?.documentManagementSettings) || [];
    const editingSetting = list.find((s) => s.id === this.props.editingDocumentManagementId);
    if (!editingSetting) {
      this.setState({ editingSetting: undefined }, () => {
        this.formRef.current?.resetFields();
        this.formRef.current?.setFieldsValue({
          requireAuditKey: false,
          maxFileSizeBytes: 524288000,
          auditLogRetentionDays: 90,
          onlyOffice: { enabled: false },
          collabora: { enabled: false },
          drawio: { serverUrl: "" }
        });
      });
      return;
    }

    this.setState({ editingSetting }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue({ ...editingSetting });
    });
  };

  existingNameValidator = async (_rule, value) => {
    const list = this.props.commonSettings?.documentManagementSettings || [];
    const isDuplicate = list.some((x) => x.name === value && x.id !== this.props.editingDocumentManagementId);
    if (isDuplicate) throw new Error("Document Management Setting name already exists.");
  };

  onFinish = async (data: any) => {
    this.saveStatus.start();

    const obj: IDocumentManagementSettings = {
      id:
        this.props.editingDocumentManagementId && this.props.editingDocumentManagementId !== ""
          ? this.props.editingDocumentManagementId
          : Guid.create().toString(),
      name: data.name,
      masterKey: data.masterKey || "",
      maxFileSizeBytes: data.maxFileSizeBytes ?? 524288000,
      requireAuditKey: !!data.requireAuditKey,
      auditLogRetentionDays: data.auditLogRetentionDays ?? 90,
      maxWatermarkFileSizeBytes: data.maxWatermarkFileSizeBytes ?? 104857600,
      chunkSizeBytes: data.chunkSizeBytes ?? 0,
      maxChunksPerUpload: data.maxChunksPerUpload ?? 10000,
      checkOutExpiryHours: data.checkOutExpiryHours ?? 24,
      wopiTokenExpiryMinutes: data.wopiTokenExpiryMinutes ?? 60,
      lockCleanupIntervalMinutes: data.lockCleanupIntervalMinutes ?? 60,
      shareRateLimiterMaxRequests: data.shareRateLimiterMaxRequests ?? 10,
      shareRateLimiterWindowSeconds: data.shareRateLimiterWindowSeconds ?? 60,
      maxAccessibleDocuments: data.maxAccessibleDocuments ?? 2000,
      onlyOffice: {
        enabled: !!data.onlyOffice?.enabled,
        documentServerUrl: data.onlyOffice?.documentServerUrl?.trim() || "",
        jwtSecret: data.onlyOffice?.jwtSecret || ""
      },
      collabora: {
        enabled: !!data.collabora?.enabled,
        serverUrl: data.collabora?.serverUrl?.trim() || "",
        wopiTokenSecret: data.collabora?.wopiTokenSecret || "",
        wopiDiscoveryUrl: data.collabora?.wopiDiscoveryUrl?.trim() || ""
      },
      drawio: {
        serverUrl: data.drawio?.serverUrl?.trim() || ""
      }
    };

    const saveParamsBase = {
      formInputName: this.props.formInputName,
      config: obj,
      configKey: "documentManagementSettings" as const,
      onError: () => this.saveStatus.fail(),
      onFinal: () => {}
    };

    if (this.props.editingDocumentManagementId) {
      this.props.handleUpdateCommonConfig({
        ...saveParamsBase,
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        }
      });
      return;
    }

    this.props.handleCreateCommonConfig({
      ...saveParamsBase,
      onSuccess: () => {
        this.saveStatus.succeed(() => {
          this.props.closeModal();
        });
      }
    });
  };

  deleteSetting = () => {
    if (!this.props.editingDocumentManagementId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingDocumentManagementId || "",
      configKey: "documentManagementSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification("Document Management setting has been deleted");
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Document Management setting"
        secondHeader="Document Management setting"
        width={720}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingDocumentManagementId ? this.deleteSetting : undefined}
            submitLabel={this.props.editingDocumentManagementId ? "UPDATE" : "CREATE"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configDocumentManagementForm"
          />
        }
      >
        <Form
          id="configDocumentManagementForm"
          name="documentManagementSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("appsettings_documentmanagement_description")}>
            {/* Name */}
            <ConfigField label="Name" required tooltipContentKey="appsettings_documentmanagement_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Document Management Setting name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Setting name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" autoFocus />
              </Form.Item>
            </ConfigField>

            {/* MasterKey */}
            <ConfigField label="Master Key (KEK)" required tooltipContentKey="appsettings_documentmanagement_masterkey">
              <Form.Item name="masterKey" rules={[{ required: true, message: "Master Key is required." }]}>
                <KuikaInput size="md" type="password" />
              </Form.Item>
            </ConfigField>

            {/* MaxFileSizeBytes */}
            <ConfigField
              label="Max File Size (bytes)"
              tooltipContentKey="appsettings_documentmanagement_maxfilesizebytes"
            >
              <Form.Item name="maxFileSizeBytes">
                <KuikaInput size="md" type="number" min={1} />
              </Form.Item>
            </ConfigField>

            {/* RequireAuditKey */}
            <ConfigField label="Require Audit Key" tooltipContentKey="appsettings_documentmanagement_requireauditkey">
              <Form.Item name="requireAuditKey" valuePropName="checked" initialValue={false}>
                <KuikaSwitch />
              </Form.Item>
            </ConfigField>

            {/* AuditLogRetentionDays */}
            <ConfigField
              label="Audit Log Retention (days)"
              tooltipContentKey="appsettings_documentmanagement_auditlogretentiondays"
            >
              <Form.Item name="auditLogRetentionDays">
                <KuikaInput size="md" type="number" min={1} />
              </Form.Item>
            </ConfigField>

            {/* OnlyOffice */}
            <ConfigModalGroupHeading>OnlyOffice</ConfigModalGroupHeading>
            <ConfigField label="Enabled" tooltipContentKey="appsettings_documentmanagement_onlyoffice_enabled">
              <Form.Item name={["onlyOffice", "enabled"]} valuePropName="checked" initialValue={false}>
                <KuikaSwitch />
              </Form.Item>
            </ConfigField>

            <Form.Item noStyle shouldUpdate={(prev, cur) => prev?.onlyOffice?.enabled !== cur?.onlyOffice?.enabled}>
              {({ getFieldValue }) => {
                const enabled = !!getFieldValue(["onlyOffice", "enabled"]);
                return (
                  <>
                    <ConfigField
                      label="Document Server URL"
                      required={enabled}
                      tooltipContentKey="appsettings_documentmanagement_onlyoffice_serverurl"
                    >
                      <Form.Item
                        name={["onlyOffice", "documentServerUrl"]}
                        rules={enabled ? [{ required: true, message: "Document Server URL is required." }] : []}
                      >
                        <KuikaInput size="md" disabled={!enabled} />
                      </Form.Item>
                    </ConfigField>
                    <ConfigField
                      label="JWT Secret"
                      required={enabled}
                      tooltipContentKey="appsettings_documentmanagement_onlyoffice_jwtsecret"
                    >
                      <Form.Item
                        name={["onlyOffice", "jwtSecret"]}
                        rules={enabled ? [{ required: true, message: "JWT Secret is required." }] : []}
                      >
                        <KuikaInput size="md" type="password" disabled={!enabled} />
                      </Form.Item>
                    </ConfigField>
                  </>
                );
              }}
            </Form.Item>

            {/* Collabora — hidden until prod-ready (WOPI temp-first loading, permissions, versionId missing) */}
            {/* Collabora config fields are preserved in form data but UI is hidden */}
            <Form.Item name={["collabora", "enabled"]} valuePropName="checked" initialValue={false} hidden>
              <KuikaSwitch />
            </Form.Item>
            <Form.Item name={["collabora", "serverUrl"]} hidden>
              <KuikaInput size="md" />
            </Form.Item>
            <Form.Item name={["collabora", "wopiTokenSecret"]} hidden>
              <KuikaInput size="md" />
            </Form.Item>
            <Form.Item name={["collabora", "wopiDiscoveryUrl"]} hidden>
              <KuikaInput size="md" />
            </Form.Item>

            {/* draw.io (diagrams.net) */}
            <ConfigModalGroupHeading>draw.io (Diagram Editor)</ConfigModalGroupHeading>
            <ConfigField label="Server URL" tooltipContentKey="appsettings_documentmanagement_drawio_serverurl">
              <Form.Item name={["drawio", "serverUrl"]}>
                <KuikaInput size="md" placeholder="https://drawio.example.com  (leave empty for embed.diagrams.net)" />
              </Form.Item>
            </ConfigField>

            {/* Advanced Settings */}
            <ConfigModalGroupHeading>Advanced Settings</ConfigModalGroupHeading>

            <ConfigField label="Max Watermark File Size (bytes)">
              <Form.Item name="maxWatermarkFileSizeBytes">
                <KuikaInput size="md" type="number" min={1} placeholder="104857600 (100 MB)" />
              </Form.Item>
            </ConfigField>

            <ConfigField label="Chunk Size (bytes)">
              <Form.Item name="chunkSizeBytes">
                <KuikaInput size="md" type="number" min={0} placeholder="4194304 (4 MB, 0 = default)" />
              </Form.Item>
            </ConfigField>

            <ConfigField label="Max Chunks Per Upload">
              <Form.Item name="maxChunksPerUpload">
                <KuikaInput size="md" type="number" min={1} placeholder="10000" />
              </Form.Item>
            </ConfigField>

            <ConfigField label="Checkout Expiry (hours)">
              <Form.Item name="checkOutExpiryHours">
                <KuikaInput size="md" type="number" min={1} placeholder="24" />
              </Form.Item>
            </ConfigField>

            <ConfigField label="WOPI Token Expiry (minutes)">
              <Form.Item name="wopiTokenExpiryMinutes">
                <KuikaInput size="md" type="number" min={1} placeholder="60" />
              </Form.Item>
            </ConfigField>

            <ConfigField label="Lock Cleanup Interval (minutes)">
              <Form.Item name="lockCleanupIntervalMinutes">
                <KuikaInput size="md" type="number" min={1} placeholder="60" />
              </Form.Item>
            </ConfigField>

            <ConfigField label="Share Rate Limiter — Max Requests">
              <Form.Item name="shareRateLimiterMaxRequests">
                <KuikaInput size="md" type="number" min={1} placeholder="10" />
              </Form.Item>
            </ConfigField>

            <ConfigField label="Share Rate Limiter — Window (seconds)">
              <Form.Item name="shareRateLimiterWindowSeconds">
                <KuikaInput size="md" type="number" min={1} placeholder="60" />
              </Form.Item>
            </ConfigField>

            <ConfigField label="Max Accessible Documents">
              <Form.Item name="maxAccessibleDocuments">
                <KuikaInput size="md" type="number" min={100} placeholder="2000" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
